import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { School, User, ShieldCheck, FileCheck, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { EDUCATION_BOARDS, BOARDS_NEEDING_DETAIL } from '@/data/educationBoards';

function SectionHeader({ icon: Icon, iconBg, title, description }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center shrink-0 mt-0.5`}>
        <Icon className="h-4.5 w-4.5 text-[#F97D6A]" />
      </div>
      <div>
        <h2 className="text-base sm:text-lg font-bold text-[#1F2937] font-['Outfit']">{title}</h2>
        {description && <p className="text-sm text-[#6B7280] mt-0.5 leading-relaxed">{description}</p>}
      </div>
    </div>
  );
}

function Field({ label, required, children, className = '' }) {
  return (
    <div className={className}>
      <Label className="text-sm font-medium text-[#374151]">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const INITIAL = {
  school_name: '', school_email: '', school_phone: '', school_address: '',
  school_city: '', school_state: '', school_pin: '', education_board: '',
  education_board_detail: '', principal_name: '',
  coord_name: '', coord_designation: '', coord_email: '', coord_mobile: '',
  safe_name: '', safe_designation: '', safe_email: '', safe_mobile: '',
  safe_same_as_coordinator: false,
  auth_use_coordinator: false, auth_use_safeguarding: false,
  auth_name: '', auth_designation: '',
  agree_participation: false, agree_safeguarding: false, // kept in state but not required for submission
};

export default function SchoolRegistrationPage() {
  const [form, setForm] = useState(INITIAL);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (field) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((p) => {
      const next = { ...p, [field]: val };
      // Keep auth rep in sync when source fields change while checkbox is checked
      if (p.auth_use_coordinator) {
        if (field === 'coord_name') next.auth_name = val;
        if (field === 'coord_designation') next.auth_designation = val;
      }
      if (p.auth_use_safeguarding) {
        if (field === 'safe_name') next.auth_name = val;
        if (field === 'safe_designation') next.auth_designation = val;
      }
      // The safeguarding contact mirrors the coordinator while "Same as Primary Coordinator" is on
      if (p.safe_same_as_coordinator && field.startsWith('coord_')) {
        const mirror = { coord_name: 'safe_name', coord_designation: 'safe_designation', coord_email: 'safe_email', coord_mobile: 'safe_mobile' }[field];
        if (mirror) next[mirror] = val;
        if (p.auth_use_safeguarding) {
          if (field === 'coord_name') next.auth_name = val;
          if (field === 'coord_designation') next.auth_designation = val;
        }
      }
      return next;
    });
    if (errors[field]) setErrors((p) => ({ ...p, [field]: undefined }));
  };

  const toggleCheck = (field) => (checked) => {
    setForm((p) => {
      const next = { ...p, [field]: checked };
      if (field === 'safe_same_as_coordinator') {
        next.safe_name = checked ? p.coord_name : '';
        next.safe_designation = checked ? p.coord_designation : '';
        next.safe_email = checked ? p.coord_email : '';
        next.safe_mobile = checked ? p.coord_mobile : '';
        // The representative mirrors the safeguarding contact, so keep it in step
        if (p.auth_use_safeguarding) {
          next.auth_name = checked ? p.coord_name : '';
          next.auth_designation = checked ? p.coord_designation : '';
        }
      }
      // If selecting auth_use_coordinator, copy coordinator details
      if (field === 'auth_use_coordinator' && checked) {
        next.auth_use_safeguarding = false;
        next.auth_name = p.coord_name;
        next.auth_designation = p.coord_designation;
      }
      // If selecting auth_use_safeguarding, copy safeguarding details
      if (field === 'auth_use_safeguarding' && checked) {
        next.auth_use_coordinator = false;
        next.auth_name = p.safe_same_as_coordinator ? p.coord_name : p.safe_name;
        next.auth_designation = p.safe_same_as_coordinator ? p.coord_designation : p.safe_designation;
      }
      // If unchecking either, clear auth fields
      if ((field === 'auth_use_coordinator' || field === 'auth_use_safeguarding') && !checked) {
        next.auth_name = '';
        next.auth_designation = '';
      }
      return next;
    });
    if (errors[field]) setErrors((p) => ({ ...p, [field]: undefined }));
  };

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const removeLogo = () => { setLogoFile(null); setLogoPreview(null); };

  const validate = () => {
    const e = {};
    const req = (f, l) => { if (!form[f]?.trim()) e[f] = `${l} is required`; };
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    const MOBILE_RE = /^(\+91[\s-]?)?[6-9]\d{9}$/;
    const PHONE_RE = /^(\+91[\s-]?)?(\d[\d\s-]{7,13}\d)$/;
    const PIN_RE = /^\d{6}$/;

    // School details
    req('school_name', 'School name');
    req('school_email', 'School email');
    if (form.school_email?.trim() && !EMAIL_RE.test(form.school_email.trim())) e.school_email = 'Enter a valid email address';
    req('school_phone', 'Phone number');
    if (form.school_phone?.trim() && !PHONE_RE.test(form.school_phone.replace(/\s/g, ''))) e.school_phone = 'Enter a valid phone number';
    req('school_address', 'Address');
    req('school_city', 'City');
    req('school_state', 'State');
    req('school_pin', 'PIN code');
    if (form.school_pin?.trim() && !PIN_RE.test(form.school_pin.trim())) e.school_pin = 'Enter a valid 6-digit PIN code';
    req('education_board', 'Education board');
    if (BOARDS_NEEDING_DETAIL.includes(form.education_board) && !form.education_board_detail?.trim()) {
      e.education_board_detail = form.education_board === 'State Board'
        ? 'Name the state board' : 'Name the board';
    }
    req('principal_name', "Principal's name");

    // Coordinator
    req('coord_name', 'Coordinator name');
    req('coord_designation', 'Designation');
    req('coord_email', 'Coordinator email');
    if (form.coord_email?.trim() && !EMAIL_RE.test(form.coord_email.trim())) e.coord_email = 'Enter a valid email address';
    req('coord_mobile', 'Mobile number');
    if (form.coord_mobile?.trim() && !MOBILE_RE.test(form.coord_mobile.replace(/[\s-]/g, ''))) e.coord_mobile = 'Enter a valid 10-digit mobile number';

    // Safeguarding contact — the same person as the coordinator needs no second set of details
    if (!form.safe_same_as_coordinator) {
      req('safe_name', 'Safeguarding contact name');
      req('safe_designation', 'Designation');
      req('safe_email', 'Safeguarding contact email');
      if (form.safe_email?.trim() && !EMAIL_RE.test(form.safe_email.trim())) e.safe_email = 'Enter a valid email address';
      req('safe_mobile', 'Mobile number');
      if (form.safe_mobile?.trim() && !MOBILE_RE.test(form.safe_mobile.replace(/[\s-]/g, ''))) e.safe_mobile = 'Enter a valid 10-digit mobile number';
    }

    // Authorized representative
    req('auth_name', 'Representative name');
    req('auth_designation', 'Designation');

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please complete all required fields');
      const firstErr = document.querySelector('[data-error="true"]');
      firstErr?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    try {
      // Resolve authorized representative from the selected source
      let resolvedAuthName = form.auth_name;
      let resolvedAuthDesignation = form.auth_designation;
      if (form.auth_use_coordinator) {
        resolvedAuthName = form.coord_name;
        resolvedAuthDesignation = form.coord_designation;
      } else if (form.auth_use_safeguarding) {
        resolvedAuthName = form.safe_same_as_coordinator ? form.coord_name : form.safe_name;
        resolvedAuthDesignation = form.safe_same_as_coordinator ? form.coord_designation : form.safe_designation;
      }
      await api.post('/school-registrations', {
        school_name: form.school_name,
        school_email: form.school_email,
        school_phone: form.school_phone,
        school_address: form.school_address,
        school_city: form.school_city,
        school_state: form.school_state,
        school_pin: form.school_pin,
        education_board: form.education_board,
        education_board_detail: form.education_board_detail,
        principal_name: form.principal_name,
        coordinator: { name: form.coord_name, designation: form.coord_designation, email: form.coord_email, mobile: form.coord_mobile },
        safeguarding: form.safe_same_as_coordinator
          ? { name: form.coord_name, designation: form.coord_designation, email: form.coord_email, mobile: form.coord_mobile }
          : { name: form.safe_name, designation: form.safe_designation, email: form.safe_email, mobile: form.safe_mobile },
        safeguarding_same_as_coordinator: form.safe_same_as_coordinator,
        auth_name: resolvedAuthName,
        auth_designation: resolvedAuthDesignation,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Submission failed. Please try again.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (field) =>
    `rounded-xl h-10 border-gray-200 text-sm ${errors[field] ? 'border-red-300 focus-visible:ring-red-300' : ''}`;

  const resolvedAuthName = () => {
    if (form.auth_use_coordinator) return form.coord_name;
    if (form.auth_use_safeguarding) return form.safe_name;
    return form.auth_name;
  };
  const resolvedAuthDesignation = () => {
    if (form.auth_use_coordinator) return form.coord_designation;
    if (form.auth_use_safeguarding) return form.safe_designation;
    return form.auth_designation;
  };

  if (submitted) {
    return (
      <div className="py-16 sm:py-24" data-testid="registration-success">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] mb-3">Registration Submitted</h1>
          <p className="text-sm text-[#6B7280] leading-relaxed mb-2">
            Thank you for registering <span className="font-semibold text-[#1F2937]">{form.school_name}</span>.
          </p>
          <p className="text-sm text-[#6B7280] leading-relaxed mb-8">
            Your application is now under review. We'll contact you within 2 working days to proceed with onboarding.
          </p>
          <a href="/" className="text-sm font-semibold text-[#F97D6A] hover:underline">Back to CareersPOV</a>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 sm:py-12" data-testid="school-registration-page">
      <div className="max-w-[720px] mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#F97D6A] bg-[#FFF0ED] px-3 py-1.5 rounded-full mb-4">
            School Onboarding
          </span>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] mb-2">
            Register Your School
          </h1>
          <p className="text-sm text-[#6B7280] max-w-md mx-auto leading-relaxed">
            Complete this form to begin your school's CareersPOV onboarding. We'll review your registration and get in touch.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8" noValidate>

          {/* ─── School Details ─── */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }} data-testid="section-school-details">
            <SectionHeader icon={School} iconBg="bg-[#FFF0ED]" title="School Details" />
            <div className="space-y-4">
              <Field label="School Name" required>
                <Input value={form.school_name} onChange={set('school_name')} placeholder="e.g. Delhi Public School, Pune" className={inputCls('school_name')} data-testid="school-name" data-error={!!errors.school_name || undefined} />
                {errors.school_name && <p className="text-xs text-red-500 mt-1">{errors.school_name}</p>}
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Official Email" required>
                  <Input type="email" value={form.school_email} onChange={set('school_email')} placeholder="info@school.edu.in" className={inputCls('school_email')} data-testid="school-email" data-error={!!errors.school_email || undefined} />
                  {errors.school_email && <p className="text-xs text-red-500 mt-1">{errors.school_email}</p>}
                </Field>
                <Field label="Phone Number" required>
                  <Input value={form.school_phone} onChange={set('school_phone')} placeholder="+91 20 1234 5678" className={inputCls('school_phone')} data-testid="school-phone" data-error={!!errors.school_phone || undefined} />
                  {errors.school_phone && <p className="text-xs text-red-500 mt-1">{errors.school_phone}</p>}
                </Field>
              </div>
              <Field label="Address" required>
                <Input value={form.school_address} onChange={set('school_address')} placeholder="Street address" className={inputCls('school_address')} data-testid="school-address" data-error={!!errors.school_address || undefined} />
                {errors.school_address && <p className="text-xs text-red-500 mt-1">{errors.school_address}</p>}
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="City" required>
                  <Input value={form.school_city} onChange={set('school_city')} placeholder="Pune" className={inputCls('school_city')} data-testid="school-city" data-error={!!errors.school_city || undefined} />
                  {errors.school_city && <p className="text-xs text-red-500 mt-1">{errors.school_city}</p>}
                </Field>
                <Field label="State" required>
                  <Input value={form.school_state} onChange={set('school_state')} placeholder="Maharashtra" className={inputCls('school_state')} data-testid="school-state" data-error={!!errors.school_state || undefined} />
                  {errors.school_state && <p className="text-xs text-red-500 mt-1">{errors.school_state}</p>}
                </Field>
                <Field label="PIN Code" required>
                  <Input value={form.school_pin} onChange={set('school_pin')} placeholder="411014" className={inputCls('school_pin')} data-testid="school-pin" data-error={!!errors.school_pin || undefined} />
                  {errors.school_pin && <p className="text-xs text-red-500 mt-1">{errors.school_pin}</p>}
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Education Board" required>
                  <select
                    value={form.education_board}
                    onChange={(e) => setForm({ ...form, education_board: e.target.value, education_board_detail: '' })}
                    className={`${inputCls('education_board')} bg-white`}
                    data-testid="school-board"
                    data-error={!!errors.education_board || undefined}
                  >
                    <option value="">Select board</option>
                    {EDUCATION_BOARDS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                  {errors.education_board && <p className="text-xs text-red-500 mt-1">{errors.education_board}</p>}
                </Field>
                {BOARDS_NEEDING_DETAIL.includes(form.education_board) && (
                  <Field label={form.education_board === 'State Board' ? 'Which State Board' : 'Which Board'} required>
                    <Input value={form.education_board_detail} onChange={set('education_board_detail')}
                           placeholder={form.education_board === 'State Board' ? 'Maharashtra State Board' : 'Name of the board'}
                           className={inputCls('education_board_detail')} data-testid="school-board-detail"
                           data-error={!!errors.education_board_detail || undefined} />
                    {errors.education_board_detail && <p className="text-xs text-red-500 mt-1">{errors.education_board_detail}</p>}
                  </Field>
                )}
              </div>
              <Field label="Principal's Name" required>
                <Input value={form.principal_name} onChange={set('principal_name')} placeholder="Full name" className={inputCls('principal_name')} data-testid="principal-name" data-error={!!errors.principal_name || undefined} />
                {errors.principal_name && <p className="text-xs text-red-500 mt-1">{errors.principal_name}</p>}
              </Field>
              <Field label="School Logo" className="pt-1">
                {logoPreview ? (
                  <div className="flex items-center gap-3">
                    <img src={logoPreview} alt="Logo preview" className="w-14 h-14 rounded-xl object-cover border border-gray-200" />
                    <button type="button" onClick={removeLogo} className="text-xs text-red-500 hover:underline flex items-center gap-1"><X className="h-3 w-3" />Remove</button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-[#6B7280] hover:text-[#4B5563] transition-colors" data-testid="logo-upload">
                    <div className="w-10 h-10 rounded-xl border border-dashed border-gray-300 flex items-center justify-center hover:border-[#F97D6A]/50 transition-colors">
                      <Upload className="h-4 w-4 text-[#9CA3AF]" />
                    </div>
                    <span>Upload logo (optional, max 2MB)</span>
                    <input type="file" accept="image/*" onChange={handleLogo} className="hidden" />
                  </label>
                )}
              </Field>
            </div>
          </div>

          {/* ─── Primary Coordinator ─── */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }} data-testid="section-coordinator">
            <SectionHeader icon={User} iconBg="bg-blue-50" title="Primary Coordinator" description="This person will manage your school's CareersPOV community, including student onboarding and mentor approvals." />
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name" required>
                  <Input value={form.coord_name} onChange={set('coord_name')} placeholder="Full name" className={inputCls('coord_name')} data-testid="coord-name" data-error={!!errors.coord_name || undefined} />
                  {errors.coord_name && <p className="text-xs text-red-500 mt-1">{errors.coord_name}</p>}
                </Field>
                <Field label="Designation" required>
                  <Input value={form.coord_designation} onChange={set('coord_designation')} placeholder="e.g. Career Counsellor" className={inputCls('coord_designation')} data-testid="coord-designation" data-error={!!errors.coord_designation || undefined} />
                  {errors.coord_designation && <p className="text-xs text-red-500 mt-1">{errors.coord_designation}</p>}
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" required>
                  <Input type="email" value={form.coord_email} onChange={set('coord_email')} placeholder="coordinator@school.edu.in" className={inputCls('coord_email')} data-testid="coord-email" data-error={!!errors.coord_email || undefined} />
                  {errors.coord_email && <p className="text-xs text-red-500 mt-1">{errors.coord_email}</p>}
                </Field>
                <Field label="Mobile" required>
                  <Input value={form.coord_mobile} onChange={set('coord_mobile')} placeholder="+91 98765 43210" className={inputCls('coord_mobile')} data-testid="coord-mobile" data-error={!!errors.coord_mobile || undefined} />
                  {errors.coord_mobile && <p className="text-xs text-red-500 mt-1">{errors.coord_mobile}</p>}
                </Field>
              </div>
            </div>
          </div>

          {/* ─── Safeguarding Contact ─── */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }} data-testid="section-safeguarding">
            <SectionHeader icon={ShieldCheck} iconBg="bg-emerald-50" title="Safeguarding Contact" description="This person handles student safety concerns, incidents, parent escalations and reporting to school authorities. They receive Career Coordinator access plus the Safeguarding permission, which unlocks the Safeguarding section inside their Coordinator Dashboard." />
            <div className="space-y-4">
              <label className="flex items-center gap-2.5 cursor-pointer" data-testid="safe-same-as-coordinator-label">
                <Checkbox checked={form.safe_same_as_coordinator} onCheckedChange={toggleCheck('safe_same_as_coordinator')} data-testid="safe-same-as-coordinator" />
                <span className="text-sm text-[#4B5563]">Same as Primary Coordinator</span>
              </label>
              {form.safe_same_as_coordinator && (
                <p className="text-xs text-emerald-700 bg-emerald-50 rounded-xl px-4 py-3" data-testid="safe-same-note">
                  {form.coord_name || 'Your Primary Coordinator'} will hold both responsibilities on one account — no second login is created.
                </p>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name" required>
                  <Input value={form.safe_name} onChange={set('safe_name')} disabled={form.safe_same_as_coordinator} placeholder="Full name" className={inputCls('safe_name')} data-testid="safe-name" data-error={!!errors.safe_name || undefined} />
                  {errors.safe_name && <p className="text-xs text-red-500 mt-1">{errors.safe_name}</p>}
                </Field>
                <Field label="Designation" required>
                  <Input value={form.safe_designation} onChange={set('safe_designation')} disabled={form.safe_same_as_coordinator} placeholder="e.g. Vice Principal" className={inputCls('safe_designation')} data-testid="safe-designation" data-error={!!errors.safe_designation || undefined} />
                  {errors.safe_designation && <p className="text-xs text-red-500 mt-1">{errors.safe_designation}</p>}
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" required>
                  <Input type="email" value={form.safe_email} onChange={set('safe_email')} disabled={form.safe_same_as_coordinator} placeholder="safeguarding@school.edu.in" className={inputCls('safe_email')} data-testid="safe-email" data-error={!!errors.safe_email || undefined} />
                  {errors.safe_email && <p className="text-xs text-red-500 mt-1">{errors.safe_email}</p>}
                </Field>
                <Field label="Mobile" required>
                  <Input value={form.safe_mobile} onChange={set('safe_mobile')} disabled={form.safe_same_as_coordinator} placeholder="+91 98765 43210" className={inputCls('safe_mobile')} data-testid="safe-mobile" data-error={!!errors.safe_mobile || undefined} />
                  {errors.safe_mobile && <p className="text-xs text-red-500 mt-1">{errors.safe_mobile}</p>}
                </Field>
              </div>
            </div>
          </div>

          {/* ─── Authorized Representative ─── */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }} data-testid="section-authorized-rep">
            <SectionHeader icon={FileCheck} iconBg="bg-violet-50" title="Authorized Representative" description="This person is authorized to accept agreements on behalf of the school." />
            <div className="space-y-4">
              <div className="flex flex-col gap-2.5 mb-1">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <Checkbox checked={form.auth_use_coordinator} onCheckedChange={toggleCheck('auth_use_coordinator')} data-testid="auth-use-coordinator" />
                  <span className="text-sm text-[#4B5563]">Use Primary Coordinator's details</span>
                </label>
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <Checkbox checked={form.auth_use_safeguarding} onCheckedChange={toggleCheck('auth_use_safeguarding')} data-testid="auth-use-safeguarding" />
                  <span className="text-sm text-[#4B5563]">Use Safeguarding Contact's details</span>
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name" required>
                  <Input value={form.auth_name} onChange={set('auth_name')} placeholder="Full name" className={inputCls('auth_name')} disabled={form.auth_use_coordinator || form.auth_use_safeguarding} data-testid="auth-name" data-error={!!errors.auth_name || undefined} />
                  {errors.auth_name && <p className="text-xs text-red-500 mt-1">{errors.auth_name}</p>}
                </Field>
                <Field label="Designation" required>
                  <Input value={form.auth_designation} onChange={set('auth_designation')} placeholder="e.g. Principal" className={inputCls('auth_designation')} disabled={form.auth_use_coordinator || form.auth_use_safeguarding} data-testid="auth-designation" data-error={!!errors.auth_designation || undefined} />
                  {errors.auth_designation && <p className="text-xs text-red-500 mt-1">{errors.auth_designation}</p>}
                </Field>
              </div>
              <p className="text-xs text-[#9CA3AF] leading-relaxed">
                By submitting this form, the authorized representative confirms they have the authority to accept agreements on behalf of the school.
              </p>
            </div>
          </div>

          {/* ─── School Agreements Info ─── */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }} data-testid="section-agreements">
            <SectionHeader icon={FileCheck} iconBg="bg-amber-50" title="School Agreements" />
            <p className="text-sm text-[#4B5563] leading-relaxed">
              After registration, we will share the <strong>School Participation Agreement</strong> and <strong>School Safeguarding Responsibilities & Acknowledgment</strong> for your School's review. Both agreements must be accepted before your School can be activated.
            </p>
          </div>

          {/* Submit */}
          <div className="text-center pt-2">
            <Button type="submit" disabled={submitting} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold px-8 py-3 h-auto text-base disabled:opacity-50" data-testid="submit-registration">
              {submitting ? 'Submitting...' : 'Submit School Registration'}
            </Button>
            <p className="text-xs text-[#9CA3AF] mt-3">
              We'll review your registration and contact you within 2 working days.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
